import type { Client } from '@urql/core'

import { graphql } from 'app/generated/gql'
import type { TaskTemplate, TaskTemplateListResult } from 'app/generated/graphql'
import { toEngineText } from 'app/lib/messageTemplates'
import { importBrowserTemplates, type BrowserTemplate } from './browserTemplateImport'
import type { ListParams } from './EntityStore'
import { EntityStore } from './EntityStore'

const LIST_QUERY = graphql(`
  query TaskTemplates($search: String, $pagination: PaginationInput, $sort: SortInput) {
    taskTemplates(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        orgId
        workspaceId
        name
        title
        description
        dueInDays
        ownerId
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query TaskTemplate($id: ID!) {
    taskTemplate(id: $id) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      title
      description
      dueInDays
      ownerId
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateTaskTemplate($input: CreateTaskTemplateInput!) {
    createTaskTemplate(input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      title
      description
      dueInDays
      ownerId
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateTaskTemplate($id: ID!, $input: UpdateTaskTemplateInput!) {
    updateTaskTemplate(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      title
      description
      dueInDays
      ownerId
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteTaskTemplate($id: ID!) {
    deleteTaskTemplate(id: $id)
  }
`)

/** The whole workspace's templates in one page, newest edit first. */
const TASK_TEMPLATE_LIST_PARAMS: ListParams = {
  pagination: { page: 0, limit: 200 },
  sort: { field: `updatedDate`, direction: `DESC` },
}

export type TaskTemplateInput = {
  name: string
  title: string
  description?: string | null
  dueInDays?: number | null
  ownerId?: string | null
}

type BrowserTaskTemplate = BrowserTemplate & TaskTemplateInput

export class TaskTemplateStore extends EntityStore<
  TaskTemplate,
  TaskTemplateListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `TaskTemplate`,
      extractList: (data: any) => data.taskTemplates,
      extractOne: (data: any) => data.taskTemplate,
      extractMutated: (data: any) => data.updateTaskTemplate ?? data.createTaskTemplate,
    })
  }

  /** The open workspace's templates. The list can still hold the previous workspace's rows
   *  for a moment after a switch, until the next fetch lands. */
  forWorkspace(workspaceId: string | undefined): TaskTemplate[] {
    return workspaceId ? this.hits.filter((t) => t.workspaceId === workspaceId) : []
  }

  /** Network-only: the server is shared with every user of the workspace, and a cached list
   *  could belong to the workspace open before a switch. */
  load() {
    return this.fetchList(TASK_TEMPLATE_LIST_PARAMS, { requestPolicy: `network-only` })
  }

  importBrowserTemplates(workspaceId: string) {
    return importBrowserTemplates<BrowserTaskTemplate, TaskTemplateInput>({
      storageKey: `trk:taskTemplates`,
      workspaceId,
      toInput: (t) => ({
        name: t.name,
        title: toEngineText(t.title, t.fallbacks),
        description: t.description ? toEngineText(t.description, t.fallbacks) : null,
        dueInDays: t.dueInDays ?? null,
        ownerId: t.ownerId ?? null,
      }),
      fetchServer: async () => {
        const result = await this.load()
        const rows = this.forWorkspace(workspaceId).map((t) => ({
          name: t.name,
          title: t.title,
          description: t.description ?? null,
          dueInDays: t.dueInDays ?? null,
          ownerId: t.ownerId ?? null,
        }))
        return { rows, failed: !!result.error }
      },
      create: (input) => this.create(input),
      sameTemplate: (a, b) =>
        a.name.trim() === b.name.trim() &&
        a.title === b.title &&
        (a.description ?? null) === (b.description ?? null) &&
        (a.dueInDays ?? null) === (b.dueInDays ?? null) &&
        (a.ownerId ?? null) === (b.ownerId ?? null),
    })
  }
}
